import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { GeoMapFeatureCollection, GeoMapRegionFeatureCollection } from './geographical-map.types';

interface CountryBackgroundRecord {
    countryId: number;
    iso2: string;
    name: string;
    regionId?: number;
    sid?: string;
}

interface GeoBackgroundLookup {
    countriesByIso: Map<string, CountryBackgroundRecord>;
    regionNamesById: Map<number, string>;
}

interface RegionAssetSource {
    url: string;
    expectedRegionId: number;
}

@Injectable()
export class GeographicalMapDataService {
    private static readonly OTHER_REGION_ID = 12;
    private static readonly regionAssetSources: RegionAssetSource[] = [
        { url: '/assets/geographical-summary/geoJson/custom.geo_Africa.json', expectedRegionId: 6 },
        { url: '/assets/geographical-summary/geoJson/custom.geo_Asia.json', expectedRegionId: 8 },
        { url: '/assets/geographical-summary/geoJson/custom.geo_Europe.json', expectedRegionId: 3 },
        { url: '/assets/geographical-summary/geoJson/custom.geo_NorthAmerica.json', expectedRegionId: 11 },
        { url: '/assets/geographical-summary/geoJson/custom.geo_Oceania.json', expectedRegionId: 9 },
        { url: '/assets/geographical-summary/geoJson/custom.geo_SouthAmerica.json', expectedRegionId: 10 },
        { url: '/assets/geographical-summary/geoJson/custom.geo_Other.json', expectedRegionId: GeographicalMapDataService.OTHER_REGION_ID },
        { url: '/assets/geographical-summary/geoJson/custom.geo_MiddleEast.json', expectedRegionId: 4 },
        { url: '/assets/geographical-summary/geoJson/custom.geo_IndianSubcontinent.json', expectedRegionId: 5 }
    ];

    private readonly worldGeometryUrl = '/assets/geographical-summary/geoJson/custom.geo_World_low.json';
    private readonly backgroundDataUrl = '/assets/geographical-summary/background-data.txt';

    constructor(private http: HttpClient) { }

    getGeometry(): Observable<GeoMapFeatureCollection> {
        return this.buildCountryGeometryFromRegionFiles().pipe(
            switchMap((geometry) => {
                if (this.hasFeatures(geometry as any)) {
                    return of(geometry);
                }

                return of({ type: 'FeatureCollection', features: [] } as any);
            }),
            map((geometry) => this.toMapFeatureCollection(geometry as any)),
            catchError(() =>
                of({
                    type: 'FeatureCollection' as const,
                    features: []
                })
            )
        );
    }

    private buildCountryGeometryFromRegionFiles(): Observable<GeoMapFeatureCollection> {
        return this.getBackgroundLookup().pipe(
            switchMap((lookup) =>
                forkJoin(
                    GeographicalMapDataService.regionAssetSources.map((source) =>
                        this.http.get<any>(source.url).pipe(
                            map((geometry) => this.toCountryFeaturesFromRegionAsset(geometry, source.expectedRegionId, lookup)),
                            catchError(() => of([] as any[]))
                        )
                    )
                ).pipe(
                    map((featureSets) => {
                        const flattened = featureSets.reduce((all, items) => all.concat(items), [] as any[]);
                        return this.toMapFeatureCollection({ type: 'FeatureCollection', features: flattened });
                    })
                )
            ),
            catchError(() =>
                of({
                    type: 'FeatureCollection' as const,
                    features: []
                })
            )
        );
    }

    private hasFeatures(geometry: { features?: any[] } | null): boolean {
        return Array.isArray(geometry?.features) && geometry!.features.length > 0;
    }

    private toMapFeatureCollection(geometry: any): GeoMapFeatureCollection {
        const features = Array.isArray(geometry?.features) ? geometry.features : [];

        return {
            type: 'FeatureCollection',
            features: features
                .filter((feature: any) => feature?.geometry && feature?.properties)
                .map((feature: any, index: number) => {
                    const name = (feature.properties.name || '').toString();
                    const sid = (feature.properties.sid || feature.properties.iso2 || '').toString().toUpperCase();
                    const iso2 = (feature.properties.iso2 || feature.properties.sid || '').toString().toUpperCase();

                    return {
                        ...feature,
                        properties: {
                            ...feature.properties,
                            eicRegionId: Number(feature.properties.eicRegionId) || GeographicalMapDataService.OTHER_REGION_ID,
                            eicCountryId: Number(feature.properties.eicCountryId) || 900000 + index,
                            name,
                            sid: sid || undefined,
                            iso2: iso2 || undefined
                        }
                    };
                })
        } as GeoMapFeatureCollection;
    }

    getRegionGeometry(): Observable<GeoMapRegionFeatureCollection> {
        return this.buildRegionGeometryFromWorld().pipe(
            switchMap((geometry) => {
                if (this.hasFeatures(geometry as any)) {
                    return of(geometry);
                }

                return of({
                    type: 'FeatureCollection' as const,
                    features: []
                });
            }),
            catchError(() =>
                of({
                    type: 'FeatureCollection' as const,
                    features: []
                })
            )
        );
    }

    private buildRegionGeometryFromWorld(): Observable<GeoMapRegionFeatureCollection> {
        return forkJoin({
            world: this.http.get<any>(this.worldGeometryUrl).pipe(catchError(() => of(null))),
            lookup: this.getBackgroundLookup()
        }).pipe(
            map(({ world, lookup }) => {
                const features = Array.isArray(world?.features) ? world.features : [];
                const regionPolygons = new Map<number, number[][][][]>();

                features.forEach((feature: any) => {
                    if (!feature?.geometry) {
                        return;
                    }

                    const iso = this.extractIso2(feature.properties);
                    const countryRecord = iso ? lookup.countriesByIso.get(iso) : undefined;
                    const regionId = countryRecord?.regionId || GeographicalMapDataService.OTHER_REGION_ID;
                    const polygons = this.normalizeGeometryToPolygons(feature.geometry);
                    if (!polygons.length) {
                        return;
                    }

                    const existing = regionPolygons.get(regionId) || [];
                    existing.push(...polygons);
                    regionPolygons.set(regionId, existing);
                });

                const regionFeatures = Array.from(regionPolygons.entries())
                    .sort(([left], [right]) => left - right)
                    .map(([regionId, polygons]) => {
                        const validPolygons = polygons
                            .map((polygon) => polygon.filter((ring) => ring.length >= 4))
                            .filter((polygon) => polygon.length > 0);

                        if (!validPolygons.length) {
                            return null;
                        }

                        const regionName = this.getDisplayRegionName(regionId, lookup.regionNamesById);
                        if (regionName.trim().toLowerCase() === 'other') {
                            return null;
                        }

                        const geometry = validPolygons.length === 1
                            ? { type: 'Polygon', coordinates: validPolygons[0] }
                            : { type: 'MultiPolygon', coordinates: validPolygons };

                        return {
                            type: 'Feature',
                            geometry,
                            properties: {
                                eicRegionId: regionId,
                                name: regionName
                            }
                        };
                    })
                    .filter((feature) => !!feature);

                return {
                    type: 'FeatureCollection' as const,
                    features: regionFeatures as any[]
                };
            }),
            catchError(() =>
                of({
                    type: 'FeatureCollection' as const,
                    features: []
                })
            )
        );
    }

    private normalizeGeometryToPolygons(geometry: any): number[][][][] {
        if (!geometry?.type || !Array.isArray(geometry.coordinates)) {
            return [];
        }

        if (geometry.type === 'Polygon') {
            return [geometry.coordinates as number[][][]];
        }

        if (geometry.type === 'MultiPolygon') {
            return geometry.coordinates as number[][][][];
        }

        return [];
    }

    private toCountryFeaturesFromRegionAsset(geometry: any, expectedRegionId: number, lookup: GeoBackgroundLookup): any[] {
        const features = Array.isArray(geometry?.features) ? geometry.features : [];

        return features
            .filter((feature: any) => feature?.geometry && feature?.properties)
            .map((feature: any, index: number) => {
                const iso = this.extractIso2(feature.properties);
                const countryRecord = iso ? lookup.countriesByIso.get(iso) : undefined;

                // Filter Antarctica by name or ISO2 code (sid)
                const name = (feature.properties.name || '').toString().trim().toLowerCase();
                const sid = ((feature.properties.sid || feature.properties.iso2 || '').toString().toUpperCase());
                if (name === 'other') {
                    return null;
                }
                if (name === 'antarctica' || sid === 'AQ') {
                    return null;
                }

                if (expectedRegionId !== GeographicalMapDataService.OTHER_REGION_ID && countryRecord?.regionId && countryRecord.regionId !== expectedRegionId) {
                    return null;
                }

                if (expectedRegionId !== GeographicalMapDataService.OTHER_REGION_ID && !countryRecord?.regionId) {
                    return null;
                }

                if (expectedRegionId === GeographicalMapDataService.OTHER_REGION_ID && countryRecord?.regionId) {
                    return null;
                }

                const eicRegionId = countryRecord?.regionId || GeographicalMapDataService.OTHER_REGION_ID;
                const resolvedName = (feature.properties.name || '').toString() || countryRecord?.name || '';
                const sourceSid = (
                    feature.properties.sid
                    || feature.properties.iso2
                    || feature.properties.postal
                    || iso
                    || ''
                ).toString().toUpperCase();
                const resolvedSid = sourceSid || (countryRecord?.sid || '').toString().toUpperCase();

                return {
                    ...feature,
                    properties: {
                        ...feature.properties,
                        eicRegionId,
                        eicCountryId: countryRecord?.countryId || (950000 + index),
                        name: resolvedName,
                        sid: resolvedSid || undefined,
                        iso2: iso || undefined
                    }
                };
            })
            .filter((feature: any) => !!feature);
    }

    private getBackgroundLookup(): Observable<GeoBackgroundLookup> {
        return this.http.get(this.backgroundDataUrl, { responseType: 'text' }).pipe(
            map((text) => this.parseBackgroundData(text)),
            catchError(() =>
                of({
                    countriesByIso: new Map<string, CountryBackgroundRecord>(),
                    regionNamesById: this.getDefaultRegionNames()
                })
            )
        );
    }

    private parseBackgroundData(text: string): GeoBackgroundLookup {
        const countriesByIso = new Map<string, CountryBackgroundRecord>();
        const regionNamesById = this.getDefaultRegionNames();

        const lines = (text || '').split(/\r?\n/);
        let inRegionSection = false;

        lines.forEach((rawLine) => {
            const line = (rawLine || '').trim();
            if (!line) {
                return;
            }

            if (/^RegionID\tRegion$/i.test(line)) {
                inRegionSection = true;
                return;
            }

            const parts = rawLine.split('\t');

            if (!inRegionSection) {
                if (!/^\d+$/.test((parts[0] || '').trim())) {
                    return;
                }

                const countryId = Number((parts[0] || '').trim());
                const iso = (parts[1] || '').trim().toUpperCase();
                const name = (parts[2] || '').trim();
                const regionRaw = (parts[3] || '').trim();
                const sidRaw = (parts[4] || '').trim();

                if (!iso || iso === 'NULL') {
                    return;
                }

                const regionId = /^\d+$/.test(regionRaw) ? Number(regionRaw) : undefined;
                const sid = sidRaw && sidRaw !== 'NULL' ? sidRaw : undefined;

                countriesByIso.set(iso, {
                    countryId,
                    iso2: iso,
                    name,
                    regionId,
                    sid
                });

                if (iso === 'UK') {
                    countriesByIso.set('GB', {
                        countryId,
                        iso2: 'GB',
                        name,
                        regionId,
                        sid
                    });
                }
                return;
            }

            if (parts.length >= 2 && /^\d+$/.test((parts[0] || '').trim())) {
                const regionId = Number((parts[0] || '').trim());
                const regionName = (parts[1] || '').trim();
                regionNamesById.set(regionId, this.normalizeRegionName(regionId, regionName));
            }
        });

        regionNamesById.set(GeographicalMapDataService.OTHER_REGION_ID, 'Other');

        return {
            countriesByIso,
            regionNamesById
        };
    }

    private getDefaultRegionNames(): Map<number, string> {
        return new Map<number, string>([
            [3, 'Europe'],
            [4, 'Middle East'],
            [5, 'Indian sub-continent'],
            [6, 'Africa'],
            [8, 'Asia'],
            [9, 'Oceania (Australasia)'],
            [10, 'South America'],
            [11, 'North America'],
            [GeographicalMapDataService.OTHER_REGION_ID, 'Other']
        ]);
    }

    private normalizeRegionName(regionId: number, name: string): string {
        if (regionId === 9) {
            return 'Oceania (Australasia)';
        }

        return name || this.getDefaultRegionNames().get(regionId) || 'Other';
    }

    private getDisplayRegionName(regionId: number, regionNamesById: Map<number, string>): string {
        if (regionId === 9) {
            return 'Oceania (Australasia)';
        }

        return regionNamesById.get(regionId) || this.getDefaultRegionNames().get(regionId) || 'Other';
    }

    private extractIso2(properties: any): string {
        const candidates = [
            properties?.iso_a2,
            properties?.iso_a2_eh,
            properties?.postal,
            properties?.iso2,
            properties?.sid
        ];

        for (const candidate of candidates) {
            const normalized = (candidate || '').toString().trim().toUpperCase();
            if (normalized && normalized !== 'NULL' && normalized !== '-99' && normalized.length === 2) {
                return normalized;
            }
        }

        return '';
    }

}
